import { Stack, StackProps } from '@chakra-ui/react';
import { SelectOption } from '@pelicargo/types';
import { useMemo } from 'react';
import { Configure, InstantSearch, useHits, useRefinementList, useSearchBox } from 'react-instantsearch-hooks';

import { useTypesense } from '../../hooks/useTypesense';
import { SimpleReactSelect } from '../SimpleReactSelect';

type ContactSelectProps = StackProps & {
  name: string;
  label: string;
};

const ContactSelect = ({ name, label, ...rest }: ContactSelectProps) => {
  const { hits } = useHits();

  useRefinementList({ attribute: 'associations.airports' });
  useRefinementList({ attribute: 'associations.accounts' });
  useRefinementList({ attribute: 'associations.providers' });
  useRefinementList({ attribute: 'associations.tags' });

  const { refine } = useSearchBox();

  const hitMap = (hit: any) => ({
    label: `${hit.preferred_name} <${hit.email}> (ID: ${hit.id})`,
    value: hit.id,
  });

  return (
    <Stack direction="column" spacing={0} {...rest}>
      <SimpleReactSelect
        name={name}
        label={label}
        onInputChange={refine}
        menuPlacement="auto"
        options={[...(hits.map(hitMap) as SelectOption[]), { label: 'None', value: null }]}
      />
    </Stack>
  );
};

type Props = StackProps & {
  // Name in SimpleForm
  name: string;
  label: string;

  // Default Search State
  defaultQuery?: string;
  defaultAirlineIds?: string[];
  defaultAirportIds?: string[];
  defaultAccountIds?: string[];
  defaultTags?: string[];
};

export const ContactInput = ({
  defaultAccountIds,
  defaultAirlineIds,
  defaultAirportIds,
  defaultTags,
  name,
  label,
  ...rest
}: Props) => {
  const refinementList = useMemo(() => {
    return {
      'associations.accounts.id': defaultAccountIds ?? [],
      'associations.providers': defaultAirlineIds ?? [],
      'associations.airports': defaultAirportIds ?? [],
      'associations.tags': defaultTags ?? [],
    };
  }, [defaultAccountIds, defaultAirlineIds, defaultAirportIds, defaultTags]);

  const { searchClient } = useTypesense({
    additionalSearchParameters: {
      query_by: 'full_name, preferred_name, email',
      facet_by: 'associations.providers, associations.airports, associations.accounts.id, associations.tags',
      sort_by: 'full_name:asc',
    },
  });

  if (!searchClient) return null;

  return (
    <InstantSearch searchClient={searchClient} indexName="contacts">
      <Configure facetsRefinements={refinementList} />
      <ContactSelect name={name} label={label} {...rest} />
    </InstantSearch>
  );
};
